// src/App.js
import React, { useState, useEffect, useMemo } from 'react';
import { Routes, Route, Link, useLocation, Navigate } from 'react-router-dom';
import 'leaflet/dist/leaflet.css';
import { useTranslation } from './i18n';

import Home from './Auth/Home';
import Login from './Auth/Login';
import Register from './Auth/Register';
import ForgotPassword from './Auth/ForgotPassword';
import PasswordReset from './Auth/PasswordReset';
import ResetPassword from './Auth/ResetPassword';

import AdminNavbar from './components/Navbars/AdminNavbar';
import TrainerProfile from './components/TrainerDashboard/TrainerProfile';
import AthleteProfile from './components/AthleteProfile/AthleteProfile';
import TrainerDashboard from './components/TrainerDashboard/TrainerDashboard';
import AthleteDashboard from './components/AthleteDashboard/AthleteDashboard';
import ActivityLinkRedirectGate from './components/ActivityLinkRedirectGate';
import TrainingZones from './components/TrainingZones/TrainingZones';
import AthleteRoutes from './routes/AthleteRoutes';
import TrainerRoutes from './routes/TrainerRoutes';
import AthleteSettings from './components/AthleteDashboard/AthleteSettings';
import ChangeImage from './components/ChangeImage';
import AcceptInvite from './components/Invitations/AcceptInvite';
import AthleteAdminPanel from './components/TrainerDashboard/AthleteAdminPanel';
import TrainerAthleteView from './components/TrainerDashboard/TrainerAthleteView';
import ErrorBoundary from './components/ErrorBoundary';
import TrainerLibraryManager from './components/TrainingLibrary/TrainerLibraryManager';

// ⬇️ Módulo social/comunidad
import SocialHub from './components/Social/SocialHub';
import SocialMeetupsPanel from './components/Social/SocialMeetupsPanel';
import SocialMeetupsBoard from './components/Social/SocialMeetupsBoard';
import CommunityProfileEditor from './components/Social/CommunityProfileEditor';
import SocialMessages from './components/Social/SocialMessages';
import MyContactsPage from './components/Social/MyContactsPage';
import ShareTrainingCardTestPage from './components/Social/SocialTrainingCard/ShareTrainingCardTestPage';
import SocialSearch from './components/Social/SocialSearch';

// ✅ DEBUG Drag&Drop
import ShareBoard from './components/Trainer/ShareBoard';

// Panel de Planes/Ciclos existente
import CycleTemplatesPanel from './components/TrainingLibrary/CycleTemplatesPanel';
import CycleTemplateCreator from './components/TrainingLibrary/CycleTemplateCreator';

// ⬇️ Página para grupos (usa GroupsPanel adentro)
import TrainerGroupsPage from './pages/TrainerGroupsPage';

// ⬇️ Página pública de unión a encuentro social desde el email
import SocialJoinEventPage from './pages/SocialJoinEventPage';

// ⬇️ NUEVO: página de detalle del encuentro (pública)
import SocialMeetupDetailPage from './pages/SocialMeetupDetailPage';

// ⬇️ Páginas legales públicas
import PrivacyEs from './components/Legal/PrivacyEs';
import PrivacyEn from './components/Legal/PrivacyEn';
import MetricsNotice from './components/Legal/MetricsNotice';



// ✅ Admin trainers panel
import TrainerAdminPanel from './components/Admin/TrainerAdminPanel';

// ✅ Super Admin panel
import SuperAdminPanel from './components/SuperAdmin/SuperAdminPanel';
// ✅ LAB panel
import LABDashboard from './components/LAB/LABDashboard';

import 'bootstrap/dist/css/bootstrap.min.css';

// ---- helpers ----
const safeRole = (v) => {
  const s = String(v || '').trim();
  if (!s || s === 'undefined' || s === 'null') return '';
  return s;
};

// ✅ CAMBIO: Admin/moderator/trainer_admin aterrizan en /admin/trainers
const roleLanding = (role) =>
  role === 'lab'
    ? '/lab'
    : role === 'trainer'
    ? (localStorage.getItem('labAccess') === 'true' ? '/lab' : '/trainer-dashboard')
    : role === 'athlete'
    ? '/athlete-dashboard'
    : role === 'superadmin'
    ? '/superadmin'
    : role === 'admin' || role === 'moderator' || role === 'trainer_admin'
    ? '/admin/trainers'
    : '/login';

// ⚠️ OJO: NO pongas /social/meetups aquí porque ese panel es protegido.
// La ruta pública real ya la declaras como /social/meetups/:eventId (más abajo).
const PUBLIC_PREFIXES = [
  '/',
  '/home',
  '/Home',
  '/login',
  '/register',
  '/forgot-password',
  '/password-reset',
  '/reset-password',
  '/accept-invite',

  // legales públicas
  '/legal/privacidad',
  '/legal/privacy',
  '/legal/aviso-metricas',

  // invitación social pública
  '/social/join-event',
];

const isPublicPath = (path) =>
  PUBLIC_PREFIXES.some((prefix) => path === prefix || path.startsWith(prefix + '/'));

const App = () => {
  const location = useLocation();
  const { t } = useTranslation();

  // ✅ estado inicial limpio
  const [userRole, setUserRole] = useState(() => safeRole(localStorage.getItem('userRole')));

  // ✅ re-sync suave cuando cambia la ruta (para el mismo tab)
  useEffect(() => {
    const current = safeRole(localStorage.getItem('userRole'));
    if (current !== userRole) setUserRole(current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ✅ sync entre pestañas
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'userRole') setUserRole(safeRole(e.newValue));
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const hideNavbar = useMemo(() => isPublicPath(location.pathname), [location.pathname]);

  // ✅ Guard: si no hay role => a login con redirect (pero NO si ya estás en /login)
  const ProtectedRoute = ({ allow, children }) => {
    const role = safeRole(userRole);

    if (!role) {
      if (location.pathname === '/login') return children;
      const redirect = encodeURIComponent(location.pathname + location.search);
      return <Navigate to={`/login?redirect=${redirect}`} replace />;
    }

    if (allow && !allow.includes(role)) {
      const target = roleLanding(role);
      if (location.pathname === target) return children;
      return <Navigate to={target} replace />;
    }

    return children;
  };

  // ✅ PublicOnly: si estás logueado y vienes a /login/register, manda a landing
  const PublicOnly = ({ children }) => {
    const role = safeRole(userRole);
    if (!role) return children;

    const target = roleLanding(role);
    if (location.pathname === target) return children;
    return <Navigate to={target} replace />;
  };

  return (
    <>
      {!hideNavbar && <AdminNavbar />}

      <ErrorBoundary>
        <Routes>
          {/* 🏠 Home */}
          <Route path="/" element={<Home />} />
          <Route path="/home" element={<Home />} />
          <Route path="/Home" element={<Home />} />

          {/* Públicas "solo si NO estás logueado" */}
          <Route
            path="/login"
            element={
              <PublicOnly>
                <Login />
              </PublicOnly>
            }
          />
          <Route
            path="/register"
            element={
              <PublicOnly>
                <Register />
              </PublicOnly>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicOnly>
                <ForgotPassword />
              </PublicOnly>
            }
          />
          <Route
            path="/password-reset"
            element={
              <PublicOnly>
                <PasswordReset />
              </PublicOnly>
            }
          />
          <Route
            path="/reset-password/:token"
            element={
              <PublicOnly>
                <ResetPassword />
              </PublicOnly>
            }
          />
          <Route
            path="/accept-invite/:token"
            element={
              <PublicOnly>
                <AcceptInvite />
              </PublicOnly>
            }
          />

          {/* 🔓 Páginas legales */}
          <Route path="/legal/privacidad" element={<PrivacyEs />} />
          <Route path="/legal/privacy" element={<PrivacyEn />} />
          <Route path="/legal/aviso-metricas" element={<MetricsNotice />} />

          {/* 🌐 pública invitaciones sociales */}
          <Route path="/social/join-event" element={<SocialJoinEventPage />} />

          {/* 🌐 pública detalle meetup */}
          <Route path="/social/meetups/:eventId" element={<SocialMeetupDetailPage />} />

          {/* ⚡ Super Admin */}
          <Route
            path="/superadmin"
            element={
              <ProtectedRoute allow={['superadmin']}>
                <SuperAdminPanel />
              </ProtectedRoute>
            }
          />

          {/* 🧪 LAB */}
          <Route
            path="/lab"
            element={
              <ProtectedRoute allow={["lab"]}>
                <LABDashboard />
              </ProtectedRoute>
            }
          />

          {/* 🛡️ Admin */}
          {/* ✅ NUEVO: ruta raíz /admin -> directo al listado */}
          <Route
            path="/admin"
            element={
              <ProtectedRoute allow={['admin', 'moderator', 'trainer_admin']}>
                <Navigate to="/admin/trainers" replace />
              </ProtectedRoute>
            }
          />

          {/* ✅ Compatibilidad: si alguien abre /admin/moderation, lo mandamos al listado */}
          <Route
            path="/admin/moderation"
            element={
              <ProtectedRoute allow={['admin', 'moderator', 'trainer_admin']}>
                <Navigate to="/admin/trainers" replace />
              </ProtectedRoute>
            }
          />

          {/* ✅ Panel real: Administración de Entrenadores */}
          <Route
            path="/admin/trainers"
            element={
              <ProtectedRoute allow={['admin', 'moderator', 'trainer_admin']}>
                <TrainerAdminPanel />
              </ProtectedRoute>
            }
          />

          {/* 🧪 TEST */}
          <Route
            path="/social/share-test"
            element={
              <ProtectedRoute allow={['athlete', 'trainer']}>
                <ShareTrainingCardTestPage />
              </ProtectedRoute>
            }
          />

          {/* ✅ Social: Search primero (para que NO lo absorba /social/*) */}
          <Route
            path="/social/search"
            element={
              <ProtectedRoute allow={['athlete', 'trainer']}>
                <SocialSearch />
              </ProtectedRoute>
            }
          />

          <Route
            path="/social/profile"
            element={
              <ProtectedRoute allow={['athlete', 'trainer']}>
                <CommunityProfileEditor />
              </ProtectedRoute>
            }
          />
          <Route
            path="/social/contacts"
            element={
              <ProtectedRoute allow={['athlete', 'trainer']}>
                <MyContactsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/social/messages"
            element={
              <ProtectedRoute allow={['athlete', 'trainer']}>
                <SocialMessages />
              </ProtectedRoute>
            }
          />
          <Route
            path="/social/meetups"
            element={
              <ProtectedRoute allow={['athlete', 'trainer']}>
                <SocialMeetupsPanel />
              </ProtectedRoute>
            }
          />
          <Route
            path="/social/meetups/board"
            element={
              <ProtectedRoute allow={['athlete', 'trainer']}>
                <SocialMeetupsBoard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/social/meetups/board-debug"
            element={
              <ProtectedRoute allow={['trainer']}>
                <ShareBoard />
              </ProtectedRoute>
            }
          />

          {/* 🌐 Social protegido (Hub / Landing) */}
          <Route
            path="/social/*"
            element={
              <ProtectedRoute allow={['athlete', 'trainer']}>
                <SocialHub />
              </ProtectedRoute>
            }
          />

          {/* Trainer */}
          <Route
            path="/trainer-dashboard"
            element={
              <ProtectedRoute allow={['trainer']}>
                <TrainerDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer-profile"
            element={
              <ProtectedRoute allow={['trainer', 'lab']}>
                <TrainerProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer-library"
            element={
              <ProtectedRoute allow={['trainer']}>
                <TrainerLibraryManager />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer-plans"
            element={
              <ProtectedRoute allow={['trainer']}>
                <CycleTemplatesPanel />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer-plans/new"
            element={
              <ProtectedRoute allow={['trainer']}>
                <CycleTemplateCreator />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer-groups"
            element={
              <ProtectedRoute allow={['trainer']}>
                <TrainerGroupsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer/*"
            element={
              <ProtectedRoute allow={['trainer']}>
                <TrainerRoutes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/trainer-athletes"
            element={
              <ProtectedRoute allow={['trainer']}>
                <AthleteAdminPanel />
              </ProtectedRoute>
            }
          />
          <Route
            path="/athlete-view/:athleteId"
            element={
              <ProtectedRoute allow={['trainer']}>
                <TrainerAthleteView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/athlete-profile/:athleteId"
            element={
              <ProtectedRoute allow={['trainer']}>
                <AthleteDashboard isTrainerView={true} />
              </ProtectedRoute>
            }
          />

          {/* Athlete */}
          <Route
            path="/athlete-dashboard"
            element={
              <ActivityLinkRedirectGate>
                <ProtectedRoute allow={['athlete']}>
                  <AthleteDashboard />
                </ProtectedRoute>
              </ActivityLinkRedirectGate>
            }
          />
          <Route
            path="/athlete-profile"
            element={
              <ProtectedRoute allow={['athlete']}>
                <AthleteProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/training-zones"
            element={
              <ProtectedRoute allow={['athlete']}>
                <TrainingZones />
              </ProtectedRoute>
            }
          />
          <Route
            path="/athlete/*"
            element={
              <ProtectedRoute allow={['athlete']}>
                <AthleteRoutes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/athlete-settings"
            element={
              <ProtectedRoute allow={['athlete']}>
                <AthleteSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-image"
            element={
              <ProtectedRoute allow={['athlete']}>
                <ChangeImage />
              </ProtectedRoute>
            }
          />

          {/* 404 */}
			<Route
			  path="*"
			  element={
				<div className="container py-5">
				  <h1>{t('common.pagina_no_encontrada')}</h1>
				  <p className="mb-0">
					{t('common.pagina_no_encontrada_descripcion')}{' '}
					<Link to="/">{t('common.backToHome')}</Link>
				  </p>
				</div>
			  }
			/>
        </Routes>
      </ErrorBoundary>
    </>
  );
};

export default App;